import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Image, Search, Video, RefreshCw, Check, X, ExternalLink,
  Camera, Film, Globe, Settings, Zap, AlertCircle,
  Key, Eye, EyeOff, Wifi, WifiOff, ChevronDown, ChevronUp
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function IntegrationCard({ name, icon, description, status, onConfigure, searchTypes, website }) {
  const [expanded, setExpanded] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);

  const isConnected = status?.status === "connected";
  const isError = status?.status === "error";
  const isLoading = !status;

  const save = async () => {
    if (!apiKey.trim()) return;
    setSaving(true);
    try {
      await onConfigure(apiKey.trim());
      setApiKey("");
      toast.success(`${name} API key guardada`);
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="glass-panel overflow-hidden">
      <div className="p-5 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              isConnected ? "bg-green-500/20" : isError ? "bg-red-500/20" : "bg-gray-500/20"
            }`}>
              {isLoading ? <RefreshCw size={22} className="text-gray-500 animate-spin" /> : 
               React.cloneElement(icon, {
                 size: 22, className: isConnected ? "text-green-400" : isError ? "text-red-400" : "text-gray-400"
               })}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-white font-semibold">{name}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  isConnected ? "bg-green-500/20 text-green-400" :
                  isError ? "bg-red-500/20 text-red-400" :
                  status?.status === "not_configured" ? "bg-yellow-500/20 text-yellow-400" :
                  "bg-gray-500/20 text-gray-400"
                }`}>
                  {isConnected ? "Conectado" : isError ? "Error" : status?.status === "not_configured" ? "Sin configurar" : "Cargando..."}
                </span>
              </div>
              <p className="text-gray-500 text-xs mt-0.5">{description}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isConnected && (
              <div className="hidden md:flex items-center gap-1.5">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-green-400 text-xs">Activo</span>
              </div>
            )}
            {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </div>
        </div>

        {isConnected && status && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            {Object.entries(status).filter(([k]) => !["status", "api_key_preview"].includes(k)).slice(0, 4).map(([k, v]) => (
              <div key={k} className="bg-black/20 rounded-lg p-2.5">
                <p className="text-gray-500 text-xs capitalize">{k.replace(/_/g, " ")}</p>
                <p className="text-white text-sm font-semibold truncate">{String(v)}</p>
              </div>
            ))}
          </div>
        )}

        {!isConnected && status?.message && (
          <div className="flex items-center gap-2 bg-red-500/10 rounded-lg p-3 mt-4">
            <AlertCircle size={14} className="text-red-400 flex-shrink-0" />
            <span className="text-red-400 text-sm">{status.message}</span>
          </div>
        )}
      </div>

      {expanded && (
        <div className="border-t border-white/10 p-5 space-y-4">
          <div>
            <label className="block text-gray-400 text-xs mb-2 font-medium flex items-center gap-1.5">
              <Key size={12} />
              {isConnected ? "Actualizar API Key" : "Configurar API Key"}
              {status?.api_key_preview && (
                <span className="text-gray-600 font-normal ml-1">· actual: {status.api_key_preview}</span>
              )}
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type={showKey ? "text" : "password"}
                  value={apiKey}
                  onChange={e => setApiKey(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && save()}
                  placeholder="Ingresa tu API key..."
                  className="input-glass w-full pr-10"
                />
                <button onClick={() => setShowKey(!showKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                  {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <button onClick={save} disabled={saving || !apiKey.trim()} className="btn-primary px-5 flex items-center gap-2">
                {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                Guardar
              </button>
            </div>
            <p className="text-gray-600 text-xs mt-1">
              Obtén tu API key en{" "}
              <a href={website} target="_blank" rel="noopener noreferrer" className="text-red-400 hover:underline">
                {website}
              </a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const ExternalIntegrationsPanel = () => {
  const [pexelsStatus, setPexelsStatus] = useState(null);
  const [serpapiStatus, setSerpapiStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searchType, setSearchType] = useState("pexels_photos");
  const [searching, setSearching] = useState(false);

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const [pexels, serpapi] = await Promise.all([
        axios.get(`${API}/admin/external-integrations/pexels/status`),
        axios.get(`${API}/admin/external-integrations/serpapi/status`)
      ]);
      setPexelsStatus(pexels.data);
      setSerpapiStatus(serpapi.data);
    } catch (e) {
      console.error("Error:", e);
    } finally { setLoading(false); }
  };

  const configurePexels = async (key) => {
    await axios.post(`${API}/admin/external-integrations/pexels/configure`, { api_key: key });
    await loadStatus();
  };

  const configureSerpAPI = async (key) => {
    await axios.post(`${API}/admin/external-integrations/serpapi/configure`, { api_key: key });
    await loadStatus();
  };

  const handleSearch = async () => {
    if (!searchQuery) return;
    setSearching(true);
    setSearchResults(null);
    try {
      const endpoints = {
        pexels_photos: `/admin/external-integrations/pexels/search/photos?query=${encodeURIComponent(searchQuery)}&per_page=12`,
        pexels_videos: `/admin/external-integrations/pexels/search/videos?query=${encodeURIComponent(searchQuery)}&per_page=8`,
        serpapi_web:   `/admin/external-integrations/serpapi/search?query=${encodeURIComponent(searchQuery)}&num=10`,
        serpapi_images:`/admin/external-integrations/serpapi/images?query=${encodeURIComponent(searchQuery)}&num=12`,
        serpapi_news:  `/admin/external-integrations/serpapi/news?query=${encodeURIComponent(searchQuery)}&num=10`,
      };
      const resp = await axios.get(`${API}${endpoints[searchType]}`);
      setSearchResults({ type: searchType, data: resp.data });
      toast.success(`Resultados encontrados`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en búsqueda");
    } finally { setSearching(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-48"><RefreshCw className="w-7 h-7 text-red-400 animate-spin" /></div>;
  }

  const pexelsReady = pexelsStatus?.status === "connected";
  const serpapiReady = serpapiStatus?.status === "connected";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">Integraciones Externas</h2>
          <p className="text-gray-500 text-sm">Pexels y SerpAPI para contenido enriquecido en tiempo real</p>
        </div>
        <button onClick={loadStatus} className="btn-secondary flex items-center gap-2 text-sm">
          <RefreshCw size={14} />Actualizar
        </button>
      </div>

      {/* Integration Cards */}
      <div className="space-y-4 mb-8">
        <IntegrationCard
          name="Pexels"
          icon={<Camera />}
          description="Millones de fotos y videos de stock de alta calidad, libres de derechos"
          status={pexelsStatus}
          onConfigure={configurePexels}
          website="https://www.pexels.com/api/"
        />
        <IntegrationCard
          name="SerpAPI"
          icon={<Search />}
          description="Búsqueda web avanzada via Google: web, imágenes, noticias y shopping"
          status={serpapiStatus}
          onConfigure={configureSerpAPI}
          website="https://serpapi.com/manage-api-key"
        />
      </div>

      {/* Test Panel */}
      <div className="glass-panel p-6">
        <h3 className="text-white font-semibold mb-1 flex items-center gap-2">
          <Zap className="text-red-400" size={18} />
          Probar Integraciones
        </h3>
        <p className="text-gray-500 text-sm mb-4">Realiza búsquedas de prueba para verificar el funcionamiento</p>

        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <select value={searchType} onChange={e => setSearchType(e.target.value)} className="input-glass md:w-52">
            <optgroup label="Pexels">
              <option value="pexels_photos" disabled={!pexelsReady}>📷 Fotos{!pexelsReady ? " (no configurado)" : ""}</option>
              <option value="pexels_videos" disabled={!pexelsReady}>🎬 Videos{!pexelsReady ? " (no configurado)" : ""}</option>
            </optgroup>
            <optgroup label="SerpAPI">
              <option value="serpapi_web" disabled={!serpapiReady}>🌐 Búsqueda Web{!serpapiReady ? " (no configurado)" : ""}</option>
              <option value="serpapi_images" disabled={!serpapiReady}>🖼️ Imágenes{!serpapiReady ? " (no configurado)" : ""}</option>
              <option value="serpapi_news" disabled={!serpapiReady}>📰 Noticias{!serpapiReady ? " (no configurado)" : ""}</option>
            </optgroup>
          </select>
          <input
            type="text" value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSearch()}
            placeholder="Buscar..."
            className="input-glass flex-1"
          />
          <button onClick={handleSearch} disabled={searching || !searchQuery} className="btn-primary flex items-center gap-2">
            {searching ? <RefreshCw className="animate-spin" size={16} /> : <Search size={16} />}
            Buscar
          </button>
        </div>

        {!pexelsReady && !serpapiReady && (
          <div className="flex items-center gap-2 bg-yellow-500/10 text-yellow-400 p-4 rounded-xl text-sm">
            <AlertCircle size={16} />
            Configura al menos una integración para poder realizar búsquedas de prueba
          </div>
        )}

        {/* Results */}
        {searchResults && (
          <div className="mt-5">
            {/* Pexels Photos */}
            {searchResults.type === "pexels_photos" && searchResults.data.photos && (
              <div>
                <p className="text-gray-400 text-sm mb-3">{searchResults.data.total_results?.toLocaleString()} fotos encontradas</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {searchResults.data.photos.map(photo => (
                    <div key={photo.id} className="relative group">
                      <img src={photo.src.medium} alt={photo.alt} className="w-full h-32 object-cover rounded-lg" />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex flex-col items-center justify-center gap-1 p-2">
                        <a href={photo.url} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/20 rounded-lg">
                          <ExternalLink size={14} className="text-white" />
                        </a>
                      </div>
                      <p className="text-gray-500 text-xs mt-1 truncate">{photo.photographer}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Pexels Videos */}
            {searchResults.type === "pexels_videos" && searchResults.data.videos && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {searchResults.data.videos.map(video => (
                  <div key={video.id} className="bg-black/20 rounded-lg p-3 flex items-center gap-3">
                    <Film className="text-red-400 flex-shrink-0" size={22} />
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium">{video.user?.name || "Desconocido"}</p>
                      <p className="text-gray-500 text-xs">{video.duration}s · {video.width}x{video.height}</p>
                      <a href={video.url} target="_blank" rel="noopener noreferrer" className="text-red-400 text-xs hover:underline">
                        Ver en Pexels →
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* SerpAPI Web */}
            {searchResults.type === "serpapi_web" && searchResults.data.organic_results && (
              <div className="space-y-3">
                {searchResults.data.organic_results.map((r, i) => (
                  <div key={i} className="bg-black/20 rounded-lg p-4">
                    <a href={r.link} target="_blank" rel="noopener noreferrer" className="text-red-400 font-medium hover:underline">{r.title}</a>
                    <p className="text-green-400 text-xs mt-1">{r.displayed_link}</p>
                    <p className="text-gray-400 text-sm mt-2">{r.snippet}</p>
                  </div>
                ))}
              </div>
            )}
            {/* SerpAPI Images */}
            {searchResults.type === "serpapi_images" && searchResults.data.images && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {searchResults.data.images.map((img, i) => (
                  <div key={i} className="relative group">
                    <img src={img.thumbnail} alt={img.title} className="w-full h-32 object-cover rounded-lg" />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                      <a href={img.original} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/20 rounded-lg">
                        <ExternalLink size={14} className="text-white" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* SerpAPI News */}
            {searchResults.type === "serpapi_news" && searchResults.data.news && (
              <div className="space-y-3">
                {searchResults.data.news.map((news, i) => (
                  <div key={i} className="bg-black/20 rounded-lg p-4 flex gap-4">
                    {news.thumbnail && <img src={news.thumbnail} alt="" className="w-16 h-16 object-cover rounded-lg flex-shrink-0" />}
                    <div>
                      <a href={news.link} target="_blank" rel="noopener noreferrer" className="text-red-400 font-medium hover:underline">{news.title}</a>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span>{news.source}</span><span>·</span><span>{news.date}</span>
                      </div>
                      <p className="text-gray-400 text-sm mt-1 line-clamp-2">{news.snippet}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExternalIntegrationsPanel;
